import { useState } from 'react'
import { formatNumber } from '../../../../../utility'
import { round } from 'lodash'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { ChevronUpIcon } from '@heroicons/react/24/outline'

type Props = {
  title: string
  value?: number
  suffix?: string
  doRound?: boolean
  items: React.ReactNode[]
  children?: React.ReactNode
}

const getNumber = (value: any, doRound: boolean, suffix: string): string => {
  if (typeof value !== 'number') return value
  if (doRound) {
    return `${formatNumber(round(value))}${suffix || ''}`
  }
  return `${formatNumber(value)}${suffix || ''}`
}

const TableItem: React.FC<Props> = ({ title, items, value, doRound = false, suffix, children }) => {
  const [show, setShow] = useState(false)

  const toggle = () => setShow(!show)

  const getCaret = () => {
    if (show) {
      return <ChevronUpIcon className="w-4 h-4" />
    }
    return <ChevronDownIcon className="w-4 h-4" />
  }

  return (
    <div>
      <div className={`flex justify-between cursor-pointer`} onClick={toggle}>
        <div className={`flex gap-1 items-center w-1/2`}>
          {getCaret()}
          {title}
        </div>
        {!!value && <div className={'w-1/2'}>{getNumber(value, doRound, suffix)}</div>}
      </div>
      {show && (
        <div className="pl-5 mb-10">
          <table className="w-full border-collapse border border-lightGray">{items}</table>
          {children && <div>{children}</div>}
        </div>
      )}
    </div>
  )
}

type TableItemRowValue = {
  value: any
  suffix?: string
  doRound?: boolean
}

type TableItemRowProps = {
  title: string
  values: TableItemRowValue[]
}

export const TableItemRow: React.FC<TableItemRowProps> = ({ title, values }) => {
  const getRowWidth = () => {
    if (values.length === 1) return 'w-1/2'
    if (values.length === 2) return 'w-1/3'
    if (values.length === 3) return 'w-1/4'
    if (values.length === 4) return 'w-1/5'
    return 'w-full'
  }

  return (
    <tr className="border-b border-lightGray">
      <td className={`${getRowWidth()} align-top border-r border-lightGray p-1`}>{title}</td>
      {values.map((value, i) => (
        <td className={`${getRowWidth()} align-top p-1 ${i < values.length - 1 && 'border-r border-lightGray'}`}>
          {getNumber(value.value, value.doRound, value.suffix)}
        </td>
      ))}
    </tr>
  )
}

export default TableItem
